import Flex from 'antd/es/flex'
import Row from 'antd/es/grid/row'
import { IdeaSummaryCard } from './idea-summary-card.tsx'
import IdeyaTypography from '../../../../common/antd/ideya-typography.tsx'
import { ValueFormatterParams } from 'ag-grid-enterprise'
import {
  compactNumberFormatter,
  currencyValueFormatter,
  percentageValueFormatter,
} from '../../../../utils/formatters.ts'
import { useAppSelector } from '../../../../redux/hooks.ts'
import { selectThemeStyle } from '../../../../features/setting/setting.slice.ts'
import Spin from 'antd/es/spin/index'
import Col from 'antd/es/grid/col'
import { SummaryInfoHelp } from './idea-list-summary-info-help.tsx'

interface IdeaSummaryDailyPnlProps {
  shouldShowSelectContributorMsg: boolean
  isLoading?: boolean
  dailyPnl: number
  totalBp: number
  wtd: number
  mtd: number
  qtd: number
  ytd: number
}

export const IdeaSummaryDailyPnlCard: React.FC<IdeaSummaryDailyPnlProps> = ({
  shouldShowSelectContributorMsg,
  isLoading,
  dailyPnl,
  totalBp,
  wtd,
  mtd,
  qtd,
  ytd,
}) => {
  const overlayComponent = shouldShowSelectContributorMsg ? (
    <SummaryInfoHelp />
  ) : isLoading ? (
    <Spin />
  ) : null

  return (
    <IdeaSummaryCard
      title={'Daily PnL'}
      overlayMsg={overlayComponent}
      textComponent={
        <div
          style={{
            width: '100%',
            display: 'flex',
            alignItems: 'flex-end',
          }}
        >
          <TextComponent dailyPnl={dailyPnl} totalBp={totalBp} />
        </div>
      }
      rows={[
        {
          label: 'WTD',
          value: <ValueRow value={wtd} percent={wtd / totalBp} />,
        },
        {
          label: 'MTD',
          value: <ValueRow value={mtd} percent={mtd / totalBp} />,
        },
        {
          label: 'QTD',
          value: <ValueRow value={qtd} percent={qtd / totalBp} />,
        },
        {
          label: 'YTD',
          value: <ValueRow value={ytd} percent={ytd / totalBp} />,
        },
      ]}
    ></IdeaSummaryCard>
  )
}

const TextComponent = ({
  dailyPnl,
  totalBp,
}: {
  dailyPnl: number
  totalBp: number
}) => {
  const themeStyle = useAppSelector(selectThemeStyle)
  const dailyPnlPercentRaw = dailyPnl / totalBp
  const dailyPnlPercent = Number.isNaN(dailyPnlPercentRaw)
    ? 0
    : dailyPnlPercentRaw

  const isZero = dailyPnlPercent === 0
  const isPositive = dailyPnlPercent > 0

  return (
    <Row gutter={[10, 0]} align={'middle'} style={{ height: '100%' }}>
      <Col>
        <IdeyaTypography.Title level={3} style={{ marginBottom: 0 }}>
          {currencyValueFormatter({ value: dailyPnl } as ValueFormatterParams, {
            decimal: 0,
            currency: 'USD',
          })}
        </IdeyaTypography.Title>
      </Col>
      <Col>
        <div
          style={{
            height: '100%',
            display: 'flex',
            alignItems: 'center',
            gap: 4,
            backgroundColor: isZero
              ? ''
              : isPositive
                ? themeStyle.colorLongBg
                : themeStyle.colorShortBg,
            borderRadius: themeStyle.borderRadiusSM,
            borderColor: isZero
              ? ''
              : isPositive
                ? themeStyle.colorLongBorder
                : themeStyle.colorShortBorder,
            padding: `0 4px`,
            color: isZero
              ? ''
              : isPositive
                ? themeStyle.colorLong
                : themeStyle.colorShort,
          }}
        >
          {isZero ? (
            <></>
          ) : isPositive ? (
            <svg
              xmlns={'http://www.w3.org/2000/svg'}
              width={'10'}
              height={'10'}
              viewBox={'0 0 10 10'}
              fill={'none'}
            >
              <path
                d={
                  'M9.92736 9.17448L5.26277 0.192818C5.12926 -0.0642727 4.87216 -0.0642727 4.73723 0.192818L0.0726438 9.17448C-0.100645 9.50941 0.0555991 10 0.335417 10H9.66458C9.9444 10 10.1006 9.50941 9.92736 9.17448Z'
                }
                fill={themeStyle.colorLong}
              />
            </svg>
          ) : (
            <svg
              xmlns={'http://www.w3.org/2000/svg'}
              width={'10'}
              height={'10'}
              viewBox={'0 0 10 10'}
              fill={'none'}
            >
              <path
                d={
                  'M9.66458 0H0.335417C0.0555991 0 -0.100645 0.490595 0.0726438 0.82552L4.73723 9.80718C4.87074 10.0643 5.12784 10.0643 5.26277 9.80718L9.92736 0.82552C10.1006 0.490595 9.9444 0 9.66458 0Z'
                }
                fill={themeStyle.colorShort}
              />
            </svg>
          )}

          {percentageValueFormatter({
            value: Math.abs(dailyPnlPercent),
          } as ValueFormatterParams)}
        </div>
      </Col>
    </Row>
  )
}

const ValueRow = ({ value, percent }: { value: number; percent: number }) => {
  const themeStyle = useAppSelector(selectThemeStyle)

  return (
    <>
      <Flex justify={'end'} gap={4} style={{ whiteSpace: 'nowrap' }}>
        {compactNumberFormatter({ value } as ValueFormatterParams)}
        <div
          style={{
            textAlign: 'right',
            color:
              percent >= 0
                ? themeStyle.agGridTableGreen
                : themeStyle.agGridTableRed,
          }}
        >
          {percent > 0 ? '+' : ''}
          {percentageValueFormatter(
            {
              value: percent,
            } as ValueFormatterParams,
            { decimal: 1 }
          )}
        </div>
      </Flex>
    </>
  )
}
